import { useState } from 'react';
import { Link } from 'react-router-dom';
import SearchBar from '@/components/ui/SearchBar';
import { SHOP_CATEGORIES, SHOP_PRODUCTS } from '@/data/content';

export default function ShopPage() {
  const [activeCategory, setActiveCategory] = useState('全部');
  const filtered = activeCategory === '全部' ? SHOP_PRODUCTS : SHOP_PRODUCTS.filter((p) => p.category === activeCategory);

  return (
    <div>
      {/* ---- Video hero ---- */}
      <div className="relative overflow-hidden w-full h-[540px] md:h-[660px] mb-8">
        <img src="/images/shop_motor_high_torque.webp" alt="" className="absolute inset-0 w-full h-full object-cover md:hidden" />
<video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover hidden md:block">
          <source src="/videos/parts-swap.webm" type="video/webm" />
<source src="/videos/parts-swap.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 flex flex-col items-center justify-center -mt-16">
          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-4 drop-shadow-[0_0_40px_rgba(0,194,255,0.5)] tracking-wider">装备库</h1>
          <p className="text-white/80 text-base md:text-lg drop-shadow-lg">官方核心件 + 社区创造 = 无限扩展</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 pb-12">
      {/* ---- Header ---- */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <h2 className="text-lg font-extrabold text-text-primary mb-2">全部装备</h2>
          <p className="text-sm text-text-secondary">官方核心件 + 社区创造 = 无限扩展。所有商品定价透明。</p>
        </div>
        <button className="flex items-center gap-2 px-4 h-9 rounded-btn bg-arena-red text-white text-sm hover:brightness-110 transition-all shrink-0">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
          购物车 (3)
        </button>
      </div>

      {/* ---- Category Filter ---- */}
      <div className="flex flex-wrap items-center gap-1.5 mb-8 sticky top-16 z-20 py-3 bg-bg-black/90 backdrop-blur-sm">
        {SHOP_CATEGORIES.map((cat) => (
          <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-3 h-7 rounded-btn text-xs font-medium transition-colors ${activeCategory === cat ? 'bg-arena-blue text-white' : 'bg-border-dark text-text-secondary hover:text-text-primary'}`}>{cat}</button>
        ))}
        <span className="ml-auto text-[11px] text-text-disabled">{filtered.length} 件商品</span>
      </div>

      {/* ---- Product Grid ---- */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-12">
        {filtered.map((p) => (
          <div key={p.id} className="bg-panel-dark/40 border border-border-dark rounded-card mecha-card mecha-bolt overflow-hidden hover:border-arena-blue/40 transition-all group cursor-pointer">
            <div className="h-36 bg-panel-mid/40 flex items-center justify-center p-4">
              <img src={p.image} alt={p.name} className="h-full w-auto object-contain group-hover:scale-105 transition-transform duration-300" />
            </div>
            <div className="p-3">
              <h3 className="text-xs font-medium text-text-primary mb-2 truncate">{p.name}</h3>
              <div className="flex items-center gap-1.5 mb-2">
                <div className="w-4 h-4 rounded-full bg-border-dark shrink-0" />
                <span className="text-[11px] text-text-secondary">{p.creator}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-tag ${p.creatorType === 'official' ? 'bg-arena-blue/15 text-arena-blue' : 'bg-border-dark text-text-secondary'}`}>{p.creatorType === 'official' ? '官方' : '社区'}</span>
              </div>
              <div className="flex items-baseline gap-1.5 mb-1.5">
                <span className="text-sm font-extrabold text-text-primary">{p.price}</span>
                {p.originalPrice && <span className="text-[11px] text-text-disabled line-through">{p.originalPrice}</span>}
              </div>
              <div className="flex items-center justify-between text-[11px] text-text-secondary">
                <span>{p.sales.toLocaleString()} 已售</span>
                <span className="text-arena-gold">{p.rating}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ---- Commission Info ---- */}
      <div className="bg-panel-dark/40 border border-border-dark rounded-card-lg mecha-card mecha-bolt p-6 mb-8">
        <h2 className="text-base font-semibold text-text-primary mb-4">佣金计划</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h3 className="text-sm font-medium text-text-primary mb-2">如何参与</h3>
            <p className="text-xs text-text-secondary leading-relaxed">设计 3D 模型、编写控制程序、改装方案——上传到模型库或商城。官方审核通过后上架销售。每笔订单自动结算佣金，创作者后台实时查看收益数据。</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-text-primary mb-2">佣金比例</h3>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-text-disabled">基础佣金（所有创作者）</span><span className="text-arena-gold font-medium">15%</span></div>
              <div className="flex justify-between"><span className="text-text-disabled">销量达门槛后</span><span className="text-arena-gold font-medium">20%</span></div>
              <div className="flex justify-between"><span className="text-text-disabled">结算周期</span><span className="text-text-secondary">月结，次月 10 日打款</span></div>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-medium text-text-primary mb-2">内容范围</h3>
            <div className="flex flex-wrap gap-1">
              {['推铲', '侧甲', '支架', '外观件', '工具件', '轮胎胎面', '悬架件', 'PID策略', '自主AI', '固件MOD'].map((tag) => (
                <span key={tag} className="text-[10px] bg-border-dark text-text-secondary px-1.5 py-0.5 rounded-tag">{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ---- Membership ---- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { t: '基础会员', p: '免费', d: '购物折扣 / 基础社区权限 / 单人赛报名', c: '#4A5560' },
          { t: '银卡会员', p: '99/年', d: '9折购物 / 优先报名 / 月度赛事数据报告 / 会员专属讨论区', c: '#88929E' },
          { t: '金卡会员', p: '199/年', d: '85折购物 / 免费认证考试 / 赛季数据分析 / 创作者加速审核', c: '#FFB800' },
          { t: '铂金会员', p: '399/年', d: '8折购物 / 全部赛事免费报名 / 专属技术支持 / 新硬件内测资格', c: '#00C2FF' },
        ].map((m) => (
          <div key={m.t} className="bg-panel-dark/40 border border-border-dark rounded-card mecha-card mecha-bolt p-5 hover:border-arena-blue/40 transition-all">
            <div className="w-2 h-2 rounded-full mb-3" style={{ backgroundColor: m.c }} />
            <h3 className="text-sm font-semibold text-text-primary">{m.t}</h3>
            <p className="text-lg font-extrabold text-arena-gold my-2">{m.p}</p>
            <p className="text-xs text-text-secondary leading-relaxed">{m.d}</p>
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}
